import { create } from "zustand";
import { AnimationValue } from "../types/Animation";
import useStoreRoomSession from "./useStoreRoomSession";

type StateStoreAnimation = {
    animations: Record<string, Array<AnimationValue & { payload?: unknown }>>,
    timeouts: Record<string, NodeJS.Timeout>
}

type StoreAnimationActions = {
    enqueue: (id: string, animation: AnimationValue & { payload?: unknown }) => void,
    dequeue: (id: string) => AnimationValue | undefined,
    flush: () => void,
}

const useStoreAnimation = create<StateStoreAnimation & StoreAnimationActions>((set, get) => ({
    animations: {},
    timeouts: {},

    enqueue: (id: string, animation: AnimationValue & { payload?: unknown }): void => {
        set(state => ({
            ...state,
            animations: {
                ...state.animations,
                [id]: [...(state.animations[id] || []), animation]
            }
        }), false);
    },

    dequeue: (id: string): AnimationValue | undefined => {
        const { animations, timeouts } = get();

        if(!animations[id] || timeouts[id]) {
            return;
        }

        const [animation] = animations[id];

        if(!animation) {
            set(state => {
                const newAnimations = { ...state.animations };
                delete newAnimations[id];
                return { ...state, animations: newAnimations };
            }, false);
            
            return;
        }

        const timeout = setTimeout(() => {
            set(state => {
                const newAnimations = { ...state.animations };
                const newTimeouts = { ...state.timeouts };
                
                if(newAnimations[id]?.length > 0) {
                    newAnimations[id] = newAnimations[id].slice(1);

                    if(newAnimations[id].length === 0) {
                        delete newAnimations[id];
                    }
                }

                delete newTimeouts[id];

                return { animations: newAnimations, timeouts: newTimeouts };
            }, false);
        }, animation.duration || 0);

        set(state => ({
            timeouts: { ...state.timeouts, [id]: timeout }
        }), false);

        return animation;
    },

    flush: (): void => {
        const { timeouts } = get();
        Object.values(timeouts).forEach(timeout => clearTimeout(timeout));
        set({ animations: {}, timeouts: {} }, false);
    },
}));

useStoreRoomSession.subscribe(
    (state) => state.roomId,
    () => {
        useStoreAnimation.getState().flush();
    }
);

export default useStoreAnimation;
